import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MdArchive,
  MdOpenInNew,
  MdOutlineArchive,
  MdOutlineStarBorder,
  MdOutlineStarPurple500,
} from 'react-icons/md'
import { Link } from 'react-router-dom'
import Config, { priceForLanguage } from '../../../lib/Config'
import { useFormatCurrency } from '../../../lib/CurrencyFormatter'
import { Accounting, Spend } from '../../../lib/Types'
import Headline from '../../Elements/Headline'
import Text from '../../Elements/Text'
import Button from '../../Formik/Button'
import Paywall from '../../Paywall'
import TrialLabel from '../TrialLabel'
import { AccountingType } from '../lib/Enums/AccountingType'
import { useAccountingBalance } from '../lib/Hooks/Accounting'
import { useUpdateSheet } from '../lib/Hooks/Sheet'
import { useExpenses } from '../lib/Hooks/Spends'
import { isOnActiveTrial, isPaymentOverdue } from '../lib/Payment'
import AccountingDevelopmentChart from './AccountingDevelopmentChart'

interface Props {
  accounting: Accounting
}

const summarizeItems = (spends: Spend[]) => {
  let total = 0

  const spendsToSummarize = spends.filter((spend) => !spend.expense_paid)

  spendsToSummarize.forEach((spend) => {
    total += spend.amounts.at(-1)!.amount
  })

  return total
}

const getFromDate = (accounting: Accounting) => {
  const date = new Date()
  switch (accounting.type) {
    case AccountingType.WEEK: {
      const day = date.getDay()
      const diff = date.getDate() - day + (day === 0 ? -6 : 1)
      return new Date(date.getFullYear(), date.getMonth(), diff)
    }
    case AccountingType.MONTH:
      return new Date(date.getFullYear(), date.getMonth(), 1)
    case AccountingType.YEAR:
      return new Date(date.getFullYear(), 0, 1)
    default:
      return new Date(accounting.created_date)
  }
}

const getToDate = (accounting: Accounting) => {
  const date = new Date()
  switch (accounting.type) {
    case AccountingType.WEEK: {
      const dayOfWeek = date.getDay()
      return dayOfWeek === 0
        ? date
        : new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() - dayOfWeek + 7,
        )
    }
    case AccountingType.MONTH:
      return new Date(date.getFullYear(), date.getMonth() + 1, 0)
    case AccountingType.YEAR:
      return new Date(date.getFullYear(), 11, 31)
    default:
      return date
  }
}

const Card: FC<Props> = ({ accounting }) => {
  const { t, i18n } = useTranslation(['accounting', 'common'])
  const formatCurrency = useFormatCurrency()
  const { data: spends } = useExpenses(accounting, {
    from: getFromDate(accounting),
    to: getToDate(accounting),
  })
  const { data: balance } = useAccountingBalance(accounting!)
  const { mutate: update } = useUpdateSheet()
  const [showPaywall, togglePaywall] = useState(false)
  const overdue = isPaymentOverdue(accounting)
  const onActiveTrial = isOnActiveTrial(accounting)
  const price = priceForLanguage(Config.PRICE_ACCOUNTING, i18n.language)

  const toggleFavorite = () => {
    const newAccounting = structuredClone(accounting)

    newAccounting.is_favorite = !newAccounting.is_favorite

    update(newAccounting)
  }

  const toggleArchive = () => {
    const newAccounting = structuredClone(accounting)

    newAccounting.is_archived = !newAccounting.is_archived

    update(newAccounting)
  }

  const getAccountingName = () => {
    let title: string
    switch (accounting.type) {
      case AccountingType.MONTH:
        title = t('accounting:card.monthlyAccounting')
        break
      case AccountingType.WEEK:
        title = t('accounting:card.weeklyAccounting')
        break
      case AccountingType.YEAR:
        title = t('accounting:card.yearlyAccounting')
        break
      default:
        title = t('accounting:card.accounting')
    }

    return `${title} #${accounting.id}`
  }

  const accountingName = accounting.name || getAccountingName()
  const purchaseName = accounting.name || t('common:paywall.accounting')

  const getAccountingSpendTitle = () => {
    switch (accounting.type) {
      case AccountingType.REGULAR:
        return t('accounting:card.spend')
      default:
        return t('accounting:card.spendForPeriod')
    }
  }

  return (
    <div
      className={`${overdue ? 'bg-trial-ended' : onActiveTrial ? 'bg-active-trial' : 'bg-gray-100'} p-4 mt-4`}
    >
      <Link to={`/accounting/${accounting.id}`}>
        <div className='flex justify-between items-start'>
          <Headline size='base' className='mb-4'>
            <strong>{accountingName}</strong>
          </Headline>
          {overdue
            ? (
              <div
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  togglePaywall(true)
                }}
              >
                <Button theme='gray'>
                  {t('common:paywall.pay', { price })}
                </Button>
              </div>
            )
            : (
              <Button theme='gray'>
                <div className='flex'>
                  <MdOpenInNew size='1.25rem' className='mr-2' />
                  {t('accounting:card.open')}
                </div>
              </Button>
            )}
        </div>
        {accounting.dedicated_account && (
          <>
            <Text size='base'>{t('accounting:card.balance')}</Text>
            <Headline size='3xl' className='mb-4'>
              {balance !== null ? formatCurrency(balance!, false) : 0}
            </Headline>
          </>
        )}
        <Text size='base'>{getAccountingSpendTitle()}</Text>
        <Headline size='4xl' className='mb-4'>
          {formatCurrency(summarizeItems(spends!), false)}
        </Headline>
        {!!accounting.balance && !!accounting.budget && (
          <div className='mt-6'>
            <Text size='base'>{t('accounting:card.remainingForPeriod')}</Text>
            <Headline size='3xl'>
              {formatCurrency(
                accounting.budget - summarizeItems(spends!),
                false,
              )}
            </Headline>
          </div>
        )}
        {accounting.type === AccountingType.REGULAR && !!accounting.budget && (
          <div className='mt-6'>
            <Text size='base'>{t('accounting:card.budget')}</Text>
            <Headline size='3xl'>
              {formatCurrency(accounting.budget, false)}
            </Headline>
          </div>
        )}
        {accounting.type !== AccountingType.REGULAR && (
          <div className='mt-6'>
            <AccountingDevelopmentChart
              accounting={accounting}
              showEmptyWarning={false}
            />
          </div>
        )}
      </Link>
      <div className='flex justify-between items-center mt-2'>
        <TrialLabel
          sheet={accounting}
          overdue={overdue}
          onActiveTrial={onActiveTrial}
        />
        <div className='flex items-center ml-auto'>
          {!accounting.is_favorite && (
            <MdOutlineStarBorder
              size='1.5rem'
              className='mr-2 cursor-pointer'
              onClick={toggleFavorite}
              title={t('accounting:card.markFavorite')}
            />
          )}
          {accounting.is_favorite && (
            <MdOutlineStarPurple500
              size='1.5rem'
              className='mr-2 cursor-pointer'
              onClick={toggleFavorite}
              title={t('accounting:card.unmarkFavorite')}
            />
          )}
          {!accounting.is_archived && (
            <MdOutlineArchive
              size='1.5rem'
              className='cursor-pointer'
              onClick={toggleArchive}
              title={t('accounting:card.archive')}
            />
          )}
          {accounting.is_archived && (
            <MdArchive
              size='1.5rem'
              className='cursor-pointer'
              onClick={toggleArchive}
              title={t('accounting:card.restore')}
            />
          )}
        </div>
      </div>
      <Paywall
        show={showPaywall}
        onHide={() => togglePaywall(false)}
        sheetType={accounting.sheet_type}
        sheetId={accounting.id}
        name={purchaseName}
      />
    </div>
  )
}

export default Card
